import {IMessage} from "@stomp/stompjs";
import {useEffect, useRef} from "react";
import {useStompClient} from "./StopmClientContext";
import {AckEvent, GameEvent} from "./events";
import {UserAction} from "../../app/types/game";

type UseGameSocketParams = {
    gameId: number;
    onGameEvent: (e: GameEvent) => void;
    onAck?: (e: AckEvent) => void;
};

export function useGameSocket({gameId, onGameEvent, onAck}: UseGameSocketParams) {
    const {client, connected, publish} = useStompClient();

    const gameIdRef = useRef(gameId);
    gameIdRef.current = gameId;

    const handlersRef = useRef({onGameEvent, onAck});
    handlersRef.current = {onGameEvent, onAck};

    useEffect(() => {
        if (!client || !connected) return;

        const gid = gameIdRef.current;

        const subPublic = client.subscribe(`/topic/game/${gid}`, (m: IMessage) =>
            handlersRef.current.onGameEvent(JSON.parse(m.body))
        );

        const subPrivate = client.subscribe(`/user/queue/game/${gid}`, (m: IMessage) =>
            handlersRef.current.onGameEvent(JSON.parse(m.body))
        );

        const subAck = client.subscribe(`/user/queue/ack/game/${gid}`, (m: IMessage) =>
            handlersRef.current.onAck?.(JSON.parse(m.body))
        );

        publish(`/app/game.${gid}.sync`, {requestId: crypto.randomUUID()});

        return () => {
            subPublic.unsubscribe();
            subPrivate.unsubscribe();
            subAck.unsubscribe();
        };
    }, [client, connected, gameId, publish]);

    const sync = (gameVersion: number) => {
        console.log("STOMP game sync", gameIdRef.current);
        return publish(`/app/game.${gameIdRef.current}.sync`, {
            requestId: crypto.randomUUID(),
            gameVersionId: gameVersion,
        });
    }

    const connect = (gameVersion: number) => {
        console.log("STOMP game connect", gameIdRef.current);
        return publish(`/app/game.${gameIdRef.current}.connect`, {
            requestId: crypto.randomUUID(),
            gameVersionId: gameVersion,
        });
    }

    const leave = () => {
        console.log("STOMP game leave", gameIdRef.current);
        publish(`/app/game.${gameIdRef.current}.leave`, {
            requestId: crypto.randomUUID(),
        });
    }

    const sendCardEvent = (
        gameVersion: number,
        userAction: UserAction,
        targetPlayerId: number,
        targetSlotId?: number,
    ) =>
        publish(`/app/game.${gameIdRef.current}.cardEvent`, {
            requestId: crypto.randomUUID(),
            gameVersionId: gameVersion,
            playerId: targetPlayerId,
            cardSlot: targetSlotId,
            userAction: userAction,
        });

    const sendSwapEvent = (
        gameVersion: number,
        targetPlayerId: number,
        swap: boolean,
    ) =>
        publish(`/app/game.${gameIdRef.current}.cardEvent`, {
            requestId: crypto.randomUUID(),
            gameVersionId: gameVersion,
            playerId: targetPlayerId,
            userAction: UserAction.SQUARE_CARD_SWAP,
            swap: swap,
        });

    const sendKnockEvent = (
        gameVersion: number,
        targetPlayerId: number,
    ) =>
        publish(`/app/game.${gameIdRef.current}.cardEvent`, {
            requestId: crypto.randomUUID(),
            gameVersionId: gameVersion,
            playerId: targetPlayerId,
            userAction: UserAction.KNOCK,
        });


    const sendEndGameEvent = (gameVersion: number) =>
        publish(`/app/game.${gameIdRef.current}.endGame`, {
            requestId: crypto.randomUUID(),
            gameVersionId: gameVersion,
        });

    return {connect, sync, leave, sendCardEvent, sendSwapEvent, sendKnockEvent, sendEndGameEvent};
}
